<script setup lang="ts">
import { computed } from 'vue'
import { Form, Field } from 'vee-validate'
import WidgetModalShell from '@/components/Dashboard/Modals/WidgetModalShell.vue'
import ButtonLoader from '@/components/Globals/Feedbacks/ButtonLoader.vue'
import type { IWidget } from '@/interfaces/components/dashboard/widget'
import type { IWidgetFormData } from '@/interfaces/components/dashboard/widget'
import { openAlarmsWidgetSchema } from '@/schemas/WidgetModalSchema'
import { useI18n } from '@/composable/useI18n'

const props = withDefaults(
  defineProps<{
    initialData?: IWidget
    loading?: boolean
  }>(),
  { initialData: undefined, loading: false },
)

const emit = defineEmits<{
  save: [formData: IWidgetFormData]
  cancel: []
}>()

const { t } = useI18n()

const fieldLabelClass = 'mb-1.5 block text-xs font-bold uppercase tracking-wider text-body'
const fieldInputClass =
  'w-full border border-stroke bg-gray-2 px-3 py-2.5 text-sm text-black outline-none focus:border-primary'
const fieldErrorClass = 'mt-1 text-sm text-red'

const modalTitle = computed(() =>
  props.initialData
    ? t('custom_dashboards.widget_modal.open_alarms.title_edit')
    : t('custom_dashboards.widget_modal.open_alarms.title_create'),
)

const initialValues = computed(() => ({
  title: props.initialData?.title ?? t('custom_dashboards.widget_modal.open_alarms.default_title'),
}))

function onSubmit(values: { title: string }) {
  emit('save', { title: values.title })
}
</script>

<template>
  <WidgetModalShell :loading="loading" @cancel="emit('cancel')">
    <template #title>
      <h2 class="font-satoshi mb-6 text-xl font-bold text-black">
        {{ modalTitle }}
      </h2>
    </template>

    <Form
      :validation-schema="openAlarmsWidgetSchema"
      :initial-values="initialValues"
      @submit="onSubmit"
    >
      <div class="space-y-5">
        <Field name="title" v-slot="{ field, errorMessage }">
          <div>
            <label :class="fieldLabelClass">
              {{ t('custom_dashboards.widget_modal.open_alarms.fields.title') }}
            </label>
            <input
              v-bind="field"
              type="text"
              :class="fieldInputClass"
              :placeholder="t('custom_dashboards.widget_modal.open_alarms.placeholders.title')"
            />
            <p v-if="errorMessage" :class="fieldErrorClass">{{ errorMessage }}</p>
          </div>
        </Field>

        <div class="border-l-4 border-blue bg-blue-50 px-4 py-3">
          <p class="text-sm leading-relaxed text-body">
            {{ t('custom_dashboards.widget_modal.open_alarms.info') }}
          </p>
        </div>
      </div>

      <div class="mt-6 border-t border-stroke pt-6">
        <div class="flex justify-end gap-4">
          <button
            type="button"
            class="text-xs font-medium uppercase tracking-wide text-body hover:text-black disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="loading"
            @click="emit('cancel')"
          >
            {{ t('custom_dashboards.widget_modal.open_alarms.actions.cancel') }}
          </button>
          <button
            type="submit"
            class="flex h-10 min-w-[7rem] items-center justify-center bg-blue px-6 py-2.5 text-xs font-medium uppercase tracking-wide text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="loading"
          >
            <ButtonLoader v-if="loading" />
            <span v-else>
              {{
                initialData
                  ? t('custom_dashboards.widget_modal.open_alarms.actions.save')
                  : t('custom_dashboards.widget_modal.open_alarms.actions.create')
              }}
            </span>
          </button>
        </div>  
      </div>
    </Form>
  </WidgetModalShell>
</template>
